import { useNavigate } from "react-router";
import { getArticulo } from "@/features/inventario/articulos/api/get-articulo";
import { useConfigurationContext } from "@/features/configuration/contexts/configuration-context";
import { notify } from "@/shared/notification";
import { Venta } from "@/shared/types/venta";

// Precarga una venta (presupuesto) en Nueva Venta, recalculando el precio con la lista actual.
export function useCargarPresupuestoEnNuevaVenta() {
    const { defaultPrecioIndex, requireVentaAuthorization } = useConfigurationContext();
    const navigate = useNavigate();

    return async (venta: Venta) => {
        try {
            // Si la organización exige autorización de venta, el índice se limita igual que en
            // el buscador de artículos: no se puede usar el precio más bajo ni precio manual (-1)
            // sin token de autorización, o el backend rechaza la venta.
            const precioIndex = requireVentaAuthorization
                ? Math.min(defaultPrecioIndex ?? 0, 1)
                : (defaultPrecioIndex ?? 0);

            const items = await Promise.all(venta.items.map(async (item) => {
                let articuloNombre = item.articuloNombre;
                let articuloCodigo = item.articuloCodigo;
                let articuloNumeroItem = item.articuloNumeroItem;
                let precioUnitario = item.precioUnitario;

                try {
                    const articulo = await getArticulo(item.articuloId);
                    articuloNombre = articulo.nombre;
                    articuloCodigo = articulo.codigo;
                    articuloNumeroItem = articulo.numeroItem ?? null;
                    const precioActual = articulo.precios?.[precioIndex];
                    // Recalcular con la lista actual; si no es válido (>0), se conserva el guardado.
                    if (precioActual != null && precioActual > 0) precioUnitario = precioActual;
                } catch {
                    // Si el artículo no se puede obtener, se usa el precio guardado del presupuesto.
                }

                return {
                    articuloId: item.articuloId,
                    articuloNombre,
                    articuloCodigo,
                    articuloNumeroItem,
                    cantidad: item.cantidad,
                    precioUnitario,
                    precioIndex
                };
            }));

            navigate("/nueva_venta", {
                state: {
                    preloadVenta: {
                        clienteId: venta.clienteId,
                        vendedorId: venta.vendedorId,
                        items
                    }
                }
            });
        } catch (error) {
            notify("Error al cargar el presupuesto en nueva venta.", { variant: "error" });
            console.error(error);
        }
    };
}
